import React from 'react';
import { Dismiss20Regular, Document20Regular, Folder20Regular } from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    uploadFilesWithProgressTray,
    uploadFolderWithProgressTray,
    validateVdrUploadFileCount,
    VDR_UPLOAD_MAX_FILES,
    VDR_UPLOAD_BATCH_SIZE,
} from '../../../utils/uploadFilesWithProgressTray';
import { filterVdrUploadFiles } from '../../../utils/vdrBulkUpload';
import styles from '../css/vdr.module.css';

/** Build a simple nested preview from webkitRelativePath list */
const buildFolderPreview = files => {
    const rootName = files[0]?.webkitRelativePath?.split('/')?.[0] || 'Folder';
    const subfolders = new Set();
    files.forEach(f => {
        const parts = (f.webkitRelativePath || f.name).split('/');
        if (parts.length > 2) subfolders.add(parts[1]);
    });
    const topFiles = files.filter(f => (f.webkitRelativePath || '').split('/').length <= 2);
    const bySub = {};
    files.forEach(f => {
        const parts = (f.webkitRelativePath || f.name).split('/');
        if (parts.length > 2) {
            const sub = parts[1];
            bySub[sub] = bySub[sub] || [];
            bySub[sub].push(parts[parts.length - 1]);
        }
    });
    return {
        rootName,
        fileCount: files.length,
        subfolderCount: subfolders.size,
        topFiles: topFiles.map(f => (f.webkitRelativePath || f.name).split('/').pop()),
        bySub,
    };
};

const countLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

/**
 * Upload files / Upload folder — Figma 6971:16130 / 6971:23491
 */
const UploadFilesModal = ({ open, onClose, workstream, folder, mode = 'files' }) => {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state?.projectData);
    const { user } = useSelector(state => state?.user);
    const { projectUsersAll } = useSelector(state => state?.team);
    const membershipId = projectUsersAll?.find(item => item?.value?.user_id === user?.id)?.value?.group_member_id;
    const [files, setFiles] = React.useState([]);
    const [dragging, setDragging] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const inputRef = React.useRef(null);

    React.useEffect(() => {
        if (open) setFiles([]);
    }, [open]);

    if (!open) return null;

    const isFolderMode = mode === 'folder';
    const destination = [workstream?.name, folder?.name].filter(Boolean).join(' → ') || workstream?.name || 'VDR';

    const onFiles = list => {
        const arr = filterVdrUploadFiles(list || []);
        const check = validateVdrUploadFileCount(isFolderMode ? arr.length : files.length + arr.length);
        if (!check.ok) {
            toast.error(check.message);
            return;
        }
        setFiles(isFolderMode ? arr : [...files, ...arr]);
    };

    const preview = isFolderMode && files.length ? buildFolderPreview(files) : null;

    const handleUpload = async () => {
        if (!files.length || !workstream?.id) {
            toast.error(isFolderMode ? 'Select a folder to upload' : 'Select files to upload');
            return;
        }
        if (!folder?.id && !isFolderMode) {
            toast.error('Select a folder first, or upload into the current folder');
            return;
        }
        const uploadable = filterVdrUploadFiles(files);
        const countCheck = validateVdrUploadFileCount(uploadable.length);
        if (!countCheck.ok) {
            toast.error(countCheck.message);
            return;
        }
        setLoading(true);
        try {
            if (isFolderMode) {
                const { started } = uploadFolderWithProgressTray({
                    dispatch,
                    files: uploadable,
                    folder,
                    workstreamId: workstream.id,
                    projectId: selectedProject?.id || localStorage['project_id'],
                    vdrId: selectedProject?.vdr?.id,
                    uploadBy: membershipId != null ? membershipId : undefined,
                    destinationLabel: destination,
                });
                if (!started) {
                    toast.error('That folder has no files to upload');
                    return;
                }
            } else {
                const result = await uploadFilesWithProgressTray({
                    files: uploadable,
                    dispatch,
                    workstreamId: workstream.id,
                    folder,
                    projectId: selectedProject?.id,
                    vdrId: selectedProject?.vdr?.id,
                    uploadBy: membershipId != null ? membershipId : undefined,
                });
                if (!result?.started && result?.message) {
                    toast.error(result.message);
                    return;
                }
            }
            onClose();
        } catch (e) {
            toast.error('Upload failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true">
            <div className={`${styles.modal} ${isFolderMode ? styles.modalTall : ''}`}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{isFolderMode ? 'Upload folder' : 'Upload files'}</h2>
                    <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>
                <div className={styles.modalBody}>
                    <p className={styles.destinationLine}>
                        Destination: <span className={styles.destinationPath}>{destination}</span>
                    </p>

                    <div
                        className={`${styles.dropZone} ${styles.dropZoneDashed} ${isFolderMode ? styles.folderDropZone : ''} ${dragging ? styles.dropZoneActive : ''}`}
                        onDragOver={e => {
                            e.preventDefault();
                            setDragging(true);
                        }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={e => {
                            e.preventDefault();
                            setDragging(false);
                            onFiles(e.dataTransfer.files);
                        }}
                        onClick={() => inputRef.current?.click()}
                    >
                        <p className={styles.dropZoneTitle}>
                            {isFolderMode ? 'Drag a folder here or choose folder' : 'Drag files here or choose files'}
                        </p>
                        {!isFolderMode && (
                            <p className={styles.dropZoneHint}>
                                PDF, DOCX, XLSX and more · up to {VDR_UPLOAD_MAX_FILES} files ({VDR_UPLOAD_BATCH_SIZE} per
                                request)
                            </p>
                        )}
                        <input
                            ref={inputRef}
                            type="file"
                            multiple={!isFolderMode}
                            {...(isFolderMode ? { webkitdirectory: '', directory: '' } : {})}
                            style={{ display: 'none' }}
                            onChange={e => onFiles(e.target.files)}
                        />
                    </div>

                    {isFolderMode && preview && (
                        <>
                            <div className={styles.folderPreview}>
                                <p className={styles.folderPreviewTitle}>
                                    {preview.rootName} · {countLabel(preview.fileCount, 'file', 'files')} ·{' '}
                                    {countLabel(preview.subfolderCount, 'subfolder', 'subfolders')}
                                </p>
                                {Object.entries(preview.bySub).map(([sub, names]) => (
                                    <div key={sub} className={styles.folderPreviewGroup}>
                                        <div className={styles.folderPreviewItem}>
                                            <Folder20Regular />
                                            <span>{sub}</span>
                                        </div>
                                        {names.map(n => (
                                            <div key={`${sub}-${n}`} className={styles.folderPreviewChild}>
                                                <Document20Regular />
                                                <span>{n}</span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                                {preview.topFiles.map(n => (
                                    <div key={n} className={styles.folderPreviewItem}>
                                        <Document20Regular />
                                        <span>{n}</span>
                                    </div>
                                ))}
                            </div>
                            <p className={styles.folderUploadNote}>Folder structure will be preserved. Each file is processed independently.</p>
                        </>
                    )}

                    {!isFolderMode && files.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                            {files.slice(0, 8).map((f, i) => (
                                <div key={`${f.name}-${i}`} className={styles.attachmentRow}>
                                    <span>
                                        <Document20Regular style={{ marginRight: 6 }} />
                                        {f.name}
                                    </span>
                                    <span className={styles.subMeta}>{(f.size / 1024).toFixed(0)} KB</span>
                                </div>
                            ))}
                            {files.length > 8 && <p className={styles.helperText}>+{files.length - 8} more</p>}
                        </div>
                    )}
                </div>
                <div className={styles.modalFooter}>
                    <button type="button" className={styles.primaryBtn} onClick={handleUpload} disabled={loading || !files.length}>
                        {isFolderMode ? 'Upload folder' : 'Upload files'}
                    </button>
                    <button type="button" className={`${styles.secondaryBtn} ${styles.modalCancel}`} onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UploadFilesModal;
